<template>
  <div class="team-effectiveness-highlights-page container maxWidth-sm text-big">
    <TeamEffectivenessReviewCreateDialog ref="createOrUpdateDialog" />
    <Modal
      v-if="latestReview"
      ref="confirmCompleteDialog"
      v-model="isConfirmingComplete"
      @hide="confirmCompleteReviewInProgress"
      title="Confirm"
      ok-text="Yes"
      cancel-text="No"
    >
      <div class="row">
        <div class="col-sm-3 text-center">
          <img
            src="../../assets/team-effectiveness/confirm-complete-review.png"
            style="width: 75%; max-width: 40vw"
            alt=""
          />
        </div>
        <div class="col-sm-9">
          <p>
            <b>Are you sure you want to close the review '{{ latestReview.name }}'?</b>
          </p>
          <p>
            No more responses will be added to this review. Your report will remain available for
            future reference.
          </p>
        </div>
      </div>
    </Modal>
    <div style="height: 40px"></div>

    <button
      v-if="isUserRelatedToTeam"
      class="btn btn-primary button-with-icon-and-text hidden-print back-button"
      @click="back"
      type="button"
    >
      <span class="glyphicon glyphicon-chevron-left"></span> Back to Team
    </button>

    <div style="width: 100%; margin: 20px 0">
      <template v-if="visible">
        <div style="border: 1px solid #eeeeee; padding: 20px">
          <h1 class="text-center" style="background-color: #eeeeee; margin: -20px; padding: 10px">
            Team Effectiveness Overview
          </h1>
          <div class="row" style="width: 100%; margin: 0">
            <div class="">
              <div style="height: 35px"></div>
              <!-- Focus: the current review in progress -->
              <div v-if="hasReviewInProgress">
                <p>
                  Your team is currently completing a Team Effectiveness Review, '{{
                    latestReview?.name
                  }}', aiming to be
                  <b>completed {{ displayDeadline(latestReview?.deadline ?? "") }}</b
                  >.
                  <span v-if="hasUserCompletedQuestionnaireForReviewInProgress"
                    >You've already completed your questionnaire.</span
                  >
                  <span v-else>You haven't yet completed your questionnaire.</span>
                  <span
                    class="text-center"
                    style="display: block; width: 100%; margin-top: 20px; margin-bottom: 20px"
                  >
                    <a
                      v-if="
                        hasReviewInProgress && !hasUserCompletedQuestionnaireForReviewInProgress
                      "
                      :href="getQuestionnaireUrl()"
                      class="btn btn-primary btn-lg"
                      data-test="complete-effectiveness-questionnaire"
                      >Complete the questionnaire</a
                    >
                  </span>
                </p>
              </div>
              <div
                v-if="hasReviewInProgress"
                v-text-from-prismic="'ter-highlights-page-top-guidance-review-in-progress'"
              ></div>
              <!-- Focus: Starting a review (if permitted) -->
              <div
                v-if="!hasReviewInProgress && !hasLatestReport"
                v-text-from-prismic="'ter-highlights-page-top-guidance-no-reviews'"
                data-test="start-new-review-section"
              ></div>
              <div
                v-if="!hasReviewInProgress && !hasLatestReport && canStartNewReview"
                class="text-center"
                data-test="start-new-review-button"
              >
                <button type="button" class="btn btn-primary btn-lg" @click="startOrEditReview">
                  Start a new review
                </button>
              </div>
              <!-- Focus: Viewing the latest report, and maybe starting a new one -->
              <div
                v-if="
                  !hasReviewInProgress && hasLatestReport && !!latestReviewWithReport?.completedDate
                "
              >
                <p>
                  Your team's latest Team Effectiveness Report ({{ latestReviewWithReport?.name }})
                  is available to view. It was completed {{ timeSinceLastCompletedReview }}.
                </p>
                <p>You can start a new review at any time.</p>
              </div>
              <div
                v-else-if="latestReview?.questionnaires.length === 1"
                data-test="need-more-responses-message"
              >
                <p>You need at least 2 responses to view the latest Team Effectiveness report.</p>
              </div>
            </div>
          </div>
          <hr v-if="hasLatestReport" />
          <div v-if="hasLatestReport" data-test="latest-report-summary">
            <div class="row">
              <div class="col-sm-12">
                <h3>Summary - {{ latestReviewWithReport?.name }}</h3>
              </div>
            </div>
            <div class="row">
              <div class="col-sm-6 col-sm-offset-3" style="padding: 0 5%">
                <HeadFromModel
                  kind="TeamEffectivenessAggregateDiamond"
                  :headId="latestReviewWithReport?.id"
                />
              </div>
            </div>
            <div class="row">
              <div class="col-sm-6 col-sm-offset-3">
                <h4>Cherish</h4>
              </div>
              <div
                class="col-sm-12 col-sm-offset-1"
                style="transform: scale(0.7); margin-top: -40px; margin-bottom: -40px"
              >
                <CherishPieChartReportVisual :reviewId="latestReviewWithReport?.id" />
              </div>
            </div>
            <div class="row">
              <div class="col-sm-6 col-sm-offset-3">
                <h4>Change</h4>
              </div>
              <div
                class="col-sm-12 col-sm-offset-1"
                style="transform: scale(0.7); margin-top: -40px; margin-bottom: -20px"
              >
                <ChangePieChartReportVisual :reviewId="latestReviewWithReport?.id" />
              </div>
            </div>
            <div class="row">
              <div class="col-sm-12 text-center">
                <a :href="getReportUrl(latestReviewWithReport)" class="btn btn-primary btn-lg"
                  >View full report</a
                >
              </div>
            </div>
          </div>
          <template v-if="hasReviewInProgress && canEditCurrentReview">
            <br />
            <hr />
            <h3 style="margin-bottom: 20px">Your review in progress - {{ latestReview?.name }}</h3>
            <div
              class="row"
              v-if="(latestReview?.questionnaires.length ?? 0) > 1"
              data-test="close-review-section"
            >
              <div class="col-sm-5 text-right">
                <button
                  type="button"
                  class="btn btn-primary btn-lg"
                  @click="completeReviewInProgress"
                >
                  Close Review
                </button>
              </div>
              <div class="col-sm-7">
                <p>
                  You have {{ latestReview?.questionnaires.length }} response{{
                    (latestReview?.questionnaires?.length ?? 0) > 1 ? "s" : ""
                  }}
                  in this review. If this is everyone who should contribute to the report, you can
                  close the review.
                </p>
              </div>
            </div>
            <div class="row">
              <div class="col-sm-5 text-right">
                <button type="button" class="btn btn-default btn-lg" @click="startOrEditReview">
                  <span class="glyphicon glyphicon-cog" aria-hidden="true"></span>&nbsp;Edit /
                  Delete Review
                </button>
              </div>
              <div class="col-sm-7">
                <p>Change the name, deadline or delete this review if it's no longer relevant.</p>
              </div>
            </div>
          </template>
          <template v-if="latestReview && canStartNewReview">
            <br />
            <hr />
            <div class="row">
              <div class="col-sm-12 text-center">
                <button type="button" class="btn btn-primary btn-lg" @click="startOrEditReview">
                  Start a new review
                </button>
              </div>
            </div>
          </template>
          <div v-if="hasHistory">
            <br />
            <hr />
            <h3>Previous reviews</h3>
            <div class="row">
              <div class="col-sm-12">
                <table class="table table-hover history-table">
                  <thead>
                    <tr role="row">
                      <th></th>
                      <th>Name</th>
                      <th>Completed</th>
                      <th>Number of Reviewers</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="review in completedReviewsSorted" :key="review.id">
                      <td class="history-table-diamond-cell">
                        <HeadFromModel
                          kind="TeamEffectivenessAggregateDiamond"
                          :headId="review.id"
                          :showTooltips="false"
                          fillOverride="solid"
                        />
                      </td>
                      <td>
                        <a :href="getReportUrl(review)" target="_blank">{{ review.name }}</a>
                      </td>
                      <td>{{ formatCompletedDate(review.completedDate ?? "") }}</td>
                      <td>{{ review.questionnaires.filter((q) => q.completedDate).length }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </template>
      <template v-else>
        <div style="height: 300px"></div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { debug as d } from "debug";
import axios from "axios";
import { Modal } from "uiv";
import moment from "dayjs";
import { computed, onMounted, ref } from "vue";
import { csContext } from "../../services/cs-context";
import { TeamEffectivenessReviewApiModel, UserTeamPermissionsApiModel } from "../../api-models";
import { settleAll } from "../../services/utilities/settle-all";
import TeamEffectivenessReviewCreateDialog from "../../components/team-effectiveness/team-effectiveness-review-create-dialog.vue";
import CherishPieChartReportVisual from "../../components/team-effectiveness/cherish-pie-chart-report-visual.vue";
import ChangePieChartReportVisual from "../../components/team-effectiveness/change-pie-chart-report-visual.vue";
import HeadFromModel from "../../components/head/head-from-model.vue";
import { getTeamEffectivenessReportFlexible } from "../../components/team-effectiveness/get-team-effectiveness-report-team-id";

const debug = d("cs.TeamEffectivenessHighlightsPage");

const reviewExists = ref(true);
const visible = ref(false);
const isConfirmingComplete = ref(false);
const latestReview = ref<TeamEffectivenessReviewApiModel | null>(null);
const latestReviewWithReport = ref<TeamEffectivenessReviewApiModel | null>(null);
const reviews = ref<TeamEffectivenessReviewApiModel[]>([]);
const userPermissions = ref<UserTeamPermissionsApiModel | null>(null);

const props = defineProps<{ id: string }>();

const teamId = computed(() => props.id ?? csContext.currentTeam?.id ?? "");

function getQuestionnaireUrl(): string {
  return `/team/effectivenessquestionnaire/${latestReview.value?.id}`;
}

function getReportUrl(review: TeamEffectivenessReviewApiModel | null): string {
  if (!review) return "";
  return `/teameffectivenessreport/view/${review.id}`;
}

const hasLatestReport = computed(
  () => !!latestReviewWithReport.value && latestReviewWithReport.value.questionnaires.length > 1,
);
const hasReviewInProgress = computed(
  () => !!latestReview.value && !latestReview.value.completedDate,
);
const isLatestReviewComplete = computed(
  () => !!latestReview.value && latestReview.value.completedDate,
);
const canStartNewReview = computed(
  () => !!(latestReview.value == null || isLatestReviewComplete.value),
);
const canEditCurrentReview = computed(() => hasReviewInProgress.value);
const isUserRelatedToTeam = computed(
  () =>
    !!userPermissions.value && (userPermissions.value.isMember || userPermissions.value.isLeader),
);
const hasUserCompletedQuestionnaireForReviewInProgress = computed(
  () =>
    !!latestReview.value &&
    latestReview.value.questionnaires.some(
      (q) => q.reviewerId === csContext.user.id && q.completedDate,
    ),
);
const completedReviewsSorted = computed(() =>
  reviews.value.filter(
    (r) => r.completedDate && r.questionnaires.filter((q) => q.completedDate).length,
  ),
);
const hasHistory = computed(
  () =>
    completedReviewsSorted.value.length > 1 ||
    (completedReviewsSorted.value.length === 1 && hasReviewInProgress.value),
);
const timeSinceLastCompletedReview = computed(() =>
  latestReviewWithReport.value && latestReviewWithReport.value.completedDate
    ? moment(latestReviewWithReport.value.completedDate).utc(true).fromNow()
    : "",
);

function getSortedReviewsByCompletionDateDescending(
  reviewData: TeamEffectivenessReviewApiModel[],
): TeamEffectivenessReviewApiModel[] {
  return reviewData.slice().sort((a, b) => {
    // Descending by completed date
    if (!a.completedDate) {
      return -1;
    }
    if (!b.completedDate) {
      return 1;
    }
    if (a.completedDate < b.completedDate) {
      return 1;
    }
    if (a.completedDate > b.completedDate) {
      return -1;
    }

    return 0;
  });
}

async function loadModel(): Promise<void> {
  debug("loadModel");
  const promises: Promise<void>[] = [];
  await axios
    .get(`/api/teameffectivenessreview/getallforteam?teamId=${teamId.value}`)
    .then((resp) => {
      const data = resp.data as TeamEffectivenessReviewApiModel[];
      latestReviewWithReport.value = null;
      if (data && data.length !== 0) {
        const sortedReviews = getSortedReviewsByCompletionDateDescending(data);
        reviewExists.value = true;
        [latestReview.value] = sortedReviews;
        reviews.value = sortedReviews;
      } else {
        reviewExists.value = false;
        latestReview.value = null;
        reviews.value = [];
      }
    });

  if (reviews.value) {
    // Check that the user is allowed to see the report (should they complete the questionnaire first, etc.?)
    const latestReviewWithPotentialReport =
      reviews.value.find((r) => r.questionnaires.length > 0) || null;
    if (latestReviewWithPotentialReport) {
      const latestReport = await getTeamEffectivenessReportFlexible(
        latestReviewWithPotentialReport.id,
      );
      latestReviewWithReport.value =
        !!latestReport && latestReport.status === "ReportAvailable"
          ? latestReviewWithPotentialReport
          : null;
    }
  }

  promises.push();

  promises.push(
    axios.get(`/api/teamuserpermissions/${teamId.value}`).then((resp) => {
      const data = resp.data as UserTeamPermissionsApiModel;
      userPermissions.value = data;
    }),
  );

  await settleAll(promises);
  visible.value = true;
}

const createOrUpdateDialog = ref<InstanceType<typeof TeamEffectivenessReviewCreateDialog> | null>(
  null,
);

function startOrEditReview(): void {
  if (!(canStartNewReview.value || canEditCurrentReview.value)) {
    return;
  }

  createOrUpdateDialog.value
    ?.open(teamId.value, hasReviewInProgress.value ? latestReview.value : null)
    .then((res) => {
      if (res) {
        loadModel().then(() => {
          setTimeout(() => {
            window.scrollTo({ top: 0 });
          }, 100);
        });
      }
    });
}

function back(): void {
  window.location.href = `/home?view=TeamDashboard&team=${teamId.value}`;
}

function completeReviewInProgress(): void {
  isConfirmingComplete.value = true;
}

function confirmCompleteReviewInProgress(msg: "cancel" | "dismiss" | "ok"): void {
  if (msg !== "ok" || !hasReviewInProgress.value) {
    return;
  }
  visible.value = false;
  axios
    .patch(`/api/teameffectivenessreview/complete?reviewId=${latestReview.value?.id}`)
    .finally(() => {
      loadModel().then(() => {
        window.scrollTo({ top: 0 });
      });
    });
}

function displayDeadline(dateString: string): string | null {
  const date = moment(dateString);
  return dateString
    ? date.calendar(
        null as any,
        {
          sameDay: "[today]",
          nextDay: "[by tomorrow]",
          nextWeek: "by dddd",
          lastDay: "[yesterday]",
          lastWeek: "[by last] dddd",
          sameElse: "[by] DD/MM/YYYY",
        } as any,
      )
    : null;
}

function formatCompletedDate(dateString: string): string {
  return moment(dateString).format("DD/MM/YYYY");
}

onMounted(async () => {
  await loadModel();
});
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="less">
@import (reference) "../../styles/bootstrap/variables.less";

.head-placeholder-image {
  width: 100%;
  margin-bottom: 20px;
}

.ter-panel-visual {
  margin: 0 auto;
  text-align: center;

  img {
    max-width: 215px;
    width: 100%;
    margin: 0 auto;
    display: block;
  }

  .btn {
    margin-top: 10px;
    margin-bottom: 10px;
  }
}

.previous-history-button {
  bottom: -80px;
  left: 10px;
}

.edit-review-button {
  font-size: 32px;
  position: absolute;
  top: 8px;
  right: 0px;
  border: none !important;
  outline: none !important;
  background-color: transparent !important;
  color: @gray;

  &:hover {
    color: @gray-dark;
  }
}

.history-table td {
  vertical-align: middle !important;
}

.history-table-diamond-cell {
  width: 100px;
}
</style>
